import React, { useCallback, useMemo, useState } from 'react';
import { Button, Box, Chip, Stack } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import PauseIcon from '@mui/icons-material/Pause';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import _ from 'lodash';
import StreamChart, { useDataFromDatasets, useStreamChartDefaultOptions } from './stream-chart';
import { useStartStopChartController } from './device-stream-chart';
import { useTranslation } from 'react-i18next';
import { useStreamRecorderContext } from './use-stream-recorder';

export const useSensorsDatasets = (dataset = []) => {
  return useMemo(() => _.map(dataset, ({ name, key, data }) => ({
    __name: name,
    __key: key,
    __compute: (scope, value, data) => {
      return value;
    },
    __data: data,
  })), [dataset]);
}

// A dataset must be like :
// {
//   __name: String,
//   __key: String,
//   __needPush: Boolean,
//   __compute: (scope, value, data) => Number, (where data is the __data array below)
//   __data: Array,
// }

const pulseKeyframes = {
  '@keyframes pulse': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.4 },
    '100%': { opacity: 1 },
  },
};

export default function SensorsChart({ datasets = [], defaultStarted = false, onStart = () => { }, onStop = () => { }, emitter, ...props }) {
  const { t } = useTranslation();
  const data = useDataFromDatasets(datasets);
  const handleStop = useCallback(() => {
    data.datasets.forEach(ds => { ds.data.length = 0; });
    onStop();
  }, [data, onStop]);
  const [pause, started, start, setPause] = useStartStopChartController(onStart, handleStop, defaultStarted);
  const [autoScale, setAutoScale] = useState(false);
  const options = useStreamChartDefaultOptions({ pause, autoScale });

  const recorder = useStreamRecorderContext();
  const recording = recorder?.recording ?? false;
  const secondsLeft = recorder?.secondsLeft ?? 0;
  const eventFlash = recorder?.eventFlash ?? null;
  const startRecording = recorder?.startRecording;
  const stopRecording = recorder?.stopRecording;

  return (
    <Box sx={{
      position: 'relative',
      ...(recording ? { border: '2px solid red', borderRadius: 1 } : {}),
    }}>
      <StreamChart {...props} data={data} options={options} emitter={emitter} />
      <Stack direction='row' spacing={1} sx={{ position: 'absolute', top: 40, left: 72, zIndex: 1 }}>
        <Button variant='contained' size='small' color={started ? 'warning' : 'success'} onClick={() => start()}>
          {started ? <StopIcon /> : <PlayArrowIcon />}
        </Button>
        {started && (
          <Button variant='contained' size='small' onClick={() => setPause(pause => !pause)}>
            {pause ? <PlayArrowIcon /> : <PauseIcon />}
          </Button>
        )}
        {started && (
          <Button variant='contained' size='small' color={autoScale ? 'primary' : 'inherit'} onClick={() => setAutoScale(v => !v)}>
            <FitScreenIcon />
          </Button>
        )}
      </Stack>

      {started && recorder && (
        <Stack direction='row' spacing={1} alignItems='center' sx={{ position: 'absolute', top: 40, right: 8, zIndex: 2 }}>
          {recording && eventFlash && (
            <Chip label={eventFlash.toUpperCase()} color='warning' size='small' />
          )}
          {recording && (
            <Box sx={{
              bgcolor: 'red',
              color: 'white',
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              fontWeight: 'bold',
              fontSize: '0.75rem',
              animation: 'pulse 1.5s infinite',
              ...pulseKeyframes,
            }}>
              ON AIR
            </Box>
          )}
          {recording && (
            <Chip label={`${secondsLeft}s`} color='error' size='small' />
          )}
          {recording && (
            <Button variant='contained' size='small' color='inherit' onClick={stopRecording}>
              <StopIcon />
            </Button>
          )}
          {!recording && (
            <Button variant='contained' size='small' color='error' onClick={startRecording}>
              <FiberManualRecordIcon />
            </Button>
          )}
        </Stack>
      )}
    </Box>
  )

}
